/**
 * Diagnostic #7 — tier-versus-cadence matrix. CLARID-SPEC-BSA-001 §3.2.1.
 *
 * "Render as a tier-versus-cadence matrix — the visual is the argument."
 *
 * The argument being made: a risk-based BSA program should show cadence getting
 * SHORTER as risk gets HIGHER. Reading left-to-right across a row, the numbers
 * should fall. Where they stay flat, the program doesn't differentiate; where
 * they rise, it differentiates backwards.
 *
 * So the matrix is built to make that gradient legible at a glance — a banker
 * should see the shape before reading a single finding.
 */

import { ExclamationTriangleIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

type RiskTier = 'LOW' | 'MODERATE_LOW' | 'MODERATE' | 'MODERATE_HIGH' | 'HIGH';

const TIER_LABEL: Record<RiskTier, string> = {
  LOW: 'Low',
  MODERATE_LOW: 'Mod-low',
  MODERATE: 'Moderate',
  MODERATE_HIGH: 'Mod-high',
  HIGH: 'High',
};

const TIER_ORDER: RiskTier[] = ['LOW', 'MODERATE_LOW', 'MODERATE', 'MODERATE_HIGH', 'HIGH'];

export interface CadenceFinding {
  type: 'FLAT' | 'INVERTED';
  category: string;
  higherTier: RiskTier;
  lowerTier: RiskTier;
  higherFrequencyMonths: number;
  lowerFrequencyMonths: number;
  narrative: string;
  higherExamples: { id: string; title: string; scopeLabel: string | null }[];
  lowerExamples: { id: string; title: string; scopeLabel: string | null }[];
}

export interface FlatCadenceData {
  findings: CadenceFinding[];
  matrix: { tier: RiskTier; category: string; frequencyMonths: number; count: number }[];
  tiersPresent: RiskTier[];
  excluded: { id: string; reason: string }[];
  comparedCount: number;
}

const CATEGORY_LABEL: Record<string, string> = {
  RISK_ASSESSMENT: 'Risk assessment',
  INDEPENDENT_AUDIT: 'Independent testing',
  TRAINING: 'Training',
  CDD_REFRESH: 'CDD refresh',
  EDD_REVIEW: 'EDD review',
  OFAC_LIST_REFRESH: 'OFAC list refresh',
  FINCEN_314A: 'FinCEN 314(a)',
  BOARD_REPORTING: 'Board reporting',
  POLICY_REVIEW: 'Policy review',
  PROCEDURE_REVIEW: 'Procedure review',
  MODEL_VALIDATION: 'Model validation',
  VENDOR_VALIDATION_EVIDENCE: 'Vendor validation',
  SYSTEM_TUNING: 'System tuning',
  BELOW_THE_LINE_TESTING: 'Below-the-line testing',
  RECORD_RETENTION: 'Record retention',
  OTHER_BSA: 'Other',
};

function cadenceLabel(months: number): string {
  if (months === 1) return 'Monthly';
  if (months === 3) return 'Quarterly';
  if (months === 6) return '6 mo';
  if (months === 12) return 'Annual';
  if (months === 24) return '2 yr';
  if (months === 36) return '3 yr';
  return `${months} mo`;
}

export default function FlatCadenceMatrix({ data }: { data: FlatCadenceData }) {
  const tiers = TIER_ORDER.filter((t) => data.tiersPresent.includes(t));
  const categories = [...new Set(data.matrix.map((m) => m.category))].sort((a, b) =>
    (CATEGORY_LABEL[a] ?? a).localeCompare(CATEGORY_LABEL[b] ?? b),
  );

  const cell = (category: string, tier: RiskTier) =>
    data.matrix.find((m) => m.category === category && m.tier === tier);

  // A row is "differentiated" when cadence strictly shortens as risk rises.
  const rowVerdict = (category: string): 'DIFFERENTIATED' | 'FLAT' | 'INVERTED' | 'SINGLE' => {
    const vals = tiers.map((t) => cell(category, t)?.frequencyMonths).filter((v): v is number => v != null);
    if (vals.length < 2) return 'SINGLE';
    let flat = false;
    for (let i = 1; i < vals.length; i++) {
      if (vals[i] > vals[i - 1]) return 'INVERTED';
      if (vals[i] === vals[i - 1]) flat = true;
    }
    return flat ? 'FLAT' : 'DIFFERENTIATED';
  };

  if (tiers.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-5 py-8 text-center">
        <p className="text-sm text-gray-600">
          No obligations carry a risk tier yet, so the program can’t be tested for risk-based differentiation.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Assign risk tiers to obligations to enable this check.
        </p>
      </div>
    );
  }

  const inverted = data.findings.filter((f) => f.type === 'INVERTED');
  const flat = data.findings.filter((f) => f.type === 'FLAT');

  return (
    <div className="space-y-4">
      {/* Headline verdict */}
      <div
        className={`rounded-lg border px-5 py-4 ${
          data.findings.length === 0
            ? 'border-emerald-200 bg-emerald-50'
            : inverted.length > 0
              ? 'border-red-200 bg-red-50'
              : 'border-amber-200 bg-amber-50'
        }`}
      >
        <div className="flex items-start gap-3">
          {data.findings.length === 0 ? (
            <ArrowTrendingDownIcon className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" aria-hidden />
          ) : (
            <ExclamationTriangleIcon
              className={`h-5 w-5 shrink-0 mt-0.5 ${inverted.length > 0 ? 'text-red-600' : 'text-amber-600'}`}
              aria-hidden
            />
          )}
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {data.findings.length === 0
                ? 'Your program differentiates by risk'
                : inverted.length > 0
                  ? `${inverted.length} area${inverted.length === 1 ? '' : 's'} where higher risk receives less attention`
                  : `${flat.length} area${flat.length === 1 ? '' : 's'} where the program does not differentiate by risk`}
            </h3>
            <p className="mt-1 text-xs text-gray-700">
              A risk-based program should show shorter cycles as risk rises. Reading each row
              left to right, the intervals should fall.
            </p>
          </div>
        </div>
      </div>

      {/* The matrix */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">
                  Program component
                </th>
                {tiers.map((t) => (
                  <th key={t} className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {TIER_LABEL[t]}
                  </th>
                ))}
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Risk-based?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((cat) => {
                const verdict = rowVerdict(cat);
                return (
                  <tr key={cat}>
                    <td className="px-4 py-2.5 text-gray-900 whitespace-nowrap">
                      {CATEGORY_LABEL[cat] ?? cat}
                    </td>
                    {tiers.map((t) => {
                      const c = cell(cat, t);
                      return (
                        <td
                          key={t}
                          className={`px-4 py-2.5 text-center tabular-nums whitespace-nowrap ${
                            c ? 'text-gray-900' : 'text-gray-300'
                          }`}
                        >
                          {c ? cadenceLabel(c.frequencyMonths) : '—'}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      {verdict === 'DIFFERENTIATED' && (
                        <span className="text-xs font-medium text-emerald-700">Yes</span>
                      )}
                      {verdict === 'FLAT' && (
                        <span className="text-xs font-medium text-amber-700">Flat</span>
                      )}
                      {verdict === 'INVERTED' && (
                        <span className="text-xs font-medium text-red-700">Inverted</span>
                      )}
                      {verdict === 'SINGLE' && <span className="text-xs text-gray-400">One tier</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Findings, worst first */}
      {data.findings.length > 0 && (
        <ul className="space-y-2">
          {data.findings.map((f, i) => (
            <li
              key={`${f.category}-${f.higherTier}-${f.lowerTier}-${i}`}
              className="rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <div className="flex items-start gap-2.5">
                <span
                  className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    f.type === 'INVERTED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {f.type === 'INVERTED' ? 'Inverted' : 'Flat'}
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-gray-900">
                    <span className="font-medium">{CATEGORY_LABEL[f.category] ?? f.category}</span>
                    {' — '}
                    {f.narrative}
                  </p>
                  {(f.higherExamples.length > 0 || f.lowerExamples.length > 0) && (
                    <p className="mt-1 text-xs text-gray-500">
                      {f.higherExamples
                        .map((e) => e.scopeLabel || e.title)
                        .filter(Boolean)
                        .join(', ')}
                      {f.lowerExamples.length > 0 && ' vs '}
                      {f.lowerExamples
                        .map((e) => e.scopeLabel || e.title)
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Scope disclosure — what was NOT compared, and why. Without this the
          diagnostic looks like it covered everything. */}
      {data.excluded.length > 0 && (
        <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <summary className="cursor-pointer text-xs font-medium text-gray-700">
            {data.comparedCount} obligation{data.comparedCount === 1 ? '' : 's'} compared ·{' '}
            {data.excluded.length} excluded
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-gray-600">
            {[...new Set(data.excluded.map((e) => e.reason))].map((reason) => (
              <li key={reason}>
                {data.excluded.filter((e) => e.reason === reason).length} — {reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
